"use strict";
/**
 * Blockchain Api Connection enables the building and accessing of the KILT [[Blockchain]] connection. In which it keeps one connection open and allows to reuse the connection for all [[Blockchain]] related tasks.
 *
 * Other modules can access the [[Blockchain]] as such: `const blockchain = await getConnectionOrConnect()`.
 *
 * @packageDocumentation
 * @module BlockchainApiConnection
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.disconnect = exports.connected = exports.clearCache = exports.getConnectionOrConnect = exports.getConnection = exports.setConnection = exports.buildConnection = void 0;
const tslib_1 = require("tslib");
const api_1 = require("@polkadot/api");
const config_1 = require("@kiltprotocol/config");
const Blockchain_1 = tslib_1.__importDefault(require("../blockchain/Blockchain"));
const TypeRegistry_1 = tslib_1.__importDefault(require("./TypeRegistry"));
let instance;
/**
 * Builds a new blockchain connection instance.
 *
 * @param host Optional host address. Otherwise taken from the ConfigService.
 * @returns A new blockchain connection instance.
 */
async function buildConnection(host = config_1.ConfigService.get('address')) {
    const provider = new api_1.WsProvider(host);
    const api = await api_1.ApiPromise.create({
        registry: TypeRegistry_1.default,
        provider,
    });
    return new Blockchain_1.default(api);
}
exports.buildConnection = buildConnection;
/**
 * Allows caching of a self-built connection instance.
 * This will be automatically used by all chain functions.
 *
 * For advanced use-cases only.
 *
 * @param connectionInstance The Blockchain instance, which should be cached.
 */
function setConnection(connectionInstance) {
    instance = connectionInstance;
}
exports.setConnection = setConnection;
/**
 * Gets the cached blockchain connection instance.
 *
 * @returns Cached blockchain connection.
 */
function getConnection() {
    return instance;
}
exports.getConnection = getConnection;
/**
 * Gets the cached blockchain connection, or builds a new one, if non-existant.
 *
 * @returns The cached or newly built blockchain connection instance.
 */
async function getConnectionOrConnect() {
    if (!instance) {
        instance = buildConnection();
    }
    return instance;
}
exports.getConnectionOrConnect = getConnectionOrConnect;
/**
 * Clears the cached blockchain connection instance.
 * This does NOT disconnect automatically beforehand!
 */
function clearCache() {
    instance = null;
}
exports.clearCache = clearCache;
/**
 * Check, if the cached blockchain connection is connected.
 *
 * @returns If there is a cached connection and it is currently connected.
 */
async function connected() {
    if (!instance)
        return false;
    const resolved = await instance;
    return resolved.api.isConnected;
}
exports.connected = connected;
/**
 * Disconnects the cached connection and clears the cache.
 *
 * @returns If there was a cached and connected connection, or not.
 */
async function disconnect() {
    const isConnected = await connected();
    if (isConnected) {
        const resolved = await instance;
        await (resolved === null || resolved === void 0 ? void 0 : resolved.api.disconnect());
    }
    clearCache();
    return isConnected;
}
exports.disconnect = disconnect;
